import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Building2, Banknote, Zap, TrendingUp, Users,
  CheckCircle, AlertTriangle, Activity, Calendar as CalendarIcon, Map
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { Spinner, Row, Col } from 'react-bootstrap';
// L.divIcon banane ke liye 'L' aur 'Marker' ko import kiya hai
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// ─── Design tokens ───────────────────
const T = {
  bg:       '#080C18',
  surface:  '#0D1120',
  border:   'rgba(255,255,255,0.06)',
  borderMd: 'rgba(255,255,255,0.10)',
  text:     '#E2E8F0',
  muted:    'rgba(255,255,255,0.35)',
  faint:    'rgba(255,255,255,0.12)',
};

const DASH_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');

  .novaa-dash * { box-sizing: border-box; }
  .novaa-dash { font-family: 'DM Sans', sans-serif; color: #E2E8F0; }

  .nd-stat-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 20px 22px;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    height: 100%;
  }
  .nd-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,0.35); }

  .nd-chart-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 22px 24px;
    height: 100%;
  }

  .nd-pending-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 16px; border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    transition: background 0.15s;
    margin-bottom: 6px;
  }
  .nd-pending-row:hover { background: rgba(255,255,255,0.06); }
  .nd-pending-row:last-child { margin-bottom: 0; }

  .nd-status-badge {
    padding: 4px 10px; border-radius: 20px;
    font-size: 9px; font-weight: 700; letter-spacing: 0.1em;
    text-transform: uppercase; font-family: 'DM Mono', monospace;
  }

  .nd-section-label {
    font-size: 9px; font-weight: 700; letter-spacing: 0.18em;
    color: rgba(255,255,255,0.25); text-transform: uppercase;
    margin-bottom: 14px;
  }

  .nd-capacity-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px; border-radius: 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
  }

  .nd-month-select {
    background: rgba(255,255,255,0.05) !important;
    border: 1px solid rgba(255,255,255,0.1) !important;
    border-radius: 9px !important;
    color: #E2E8F0 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 7px 12px !important;
    cursor: pointer !important;
    outline: none !important;
  }
  .nd-month-select:focus { border-color: rgba(96,165,250,0.5) !important; }
  .nd-month-select option { background: #1E2535; color: #E2E8F0; }

  .nd-refresh-btn {
    width: 34px; height: 34px; border-radius: 9px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: rgba(255,255,255,0.5);
    transition: all 0.15s;
  }
  .nd-refresh-btn:hover { background: rgba(255,255,255,0.1); color: #E2E8F0; }

  .nd-progress-track {
    height: 6px; border-radius: 6px;
    background: rgba(255,255,255,0.07);
    overflow: hidden; margin-top: 6px;
  }
  .nd-progress-fill {
    height: 100%; border-radius: 6px;
    background: linear-gradient(90deg, #3B82F6, #22D3A5);
    transition: width 0.8s cubic-bezier(0.34,1.3,0.64,1);
  }

  /* Blinking Animation for Map Markers */
  @keyframes ndPulse {
    0% { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
    70% { transform: scale(1); box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
    100% { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  }
  .nd-map-marker {
    position: absolute;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.3);
    animation: ndPulse 1.5s infinite cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Remove Leaflet's default white background from custom DivIcon */
  .transparent-marker {
    background: transparent !important;
    border: none !important;
  }

  .fade-up { animation: ndFadeUp 0.4s cubic-bezier(0.34,1.2,0.64,1) both; }
  @keyframes ndFadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  .stagger-1 { animation-delay: 0.05s; }
  .stagger-2 { animation-delay: 0.10s; }
  .stagger-3 { animation-delay: 0.15s; }
  .stagger-4 { animation-delay: 0.20s; }
`;

// ─── Status badge colours ─────────────────────────────────────────
const statusStyle = (status) => {
  const map = {
    scheduled:     { color: '#94A3B8', bg: 'rgba(148,163,184,0.15)' },
    pending:       { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)'  },
    before_photos: { color: '#A78BFA', bg: 'rgba(167,139,250,0.15)' },
    after_photos:  { color: '#F9A8D4', bg: 'rgba(249,168,212,0.15)' },
    in_progress:   { color: '#60A5FA', bg: 'rgba(96,165,250,0.15)'  },
    completed:     { color: '#22D3A5', bg: 'rgba(34,211,165,0.15)'  },
    missed:        { color: '#F87171', bg: 'rgba(248,113,113,0.15)' },
  };
  return map[status?.toLowerCase()] || { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)' };
};

// Custom Tooltip
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4, letterSpacing: '0.08em' }}>DAY {label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#60A5FA', fontFamily: "'DM Mono', monospace" }}>{payload[0].value} jobs</div>
    </div>
  );
};

// Stat Card
const StatCard = ({ title, value, icon: Icon, accentColor, trend, stagger }) => (
  <div className={`nd-stat-card fade-up stagger-${stagger}`}
    style={{ borderTop: `2px solid ${accentColor}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.18em', marginBottom: 8 }}>
          {title.toUpperCase()}
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: '-0.03em', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
          {value}
        </div>
      </div>
      <div style={{ width: 42, height: 42, borderRadius: 11, background: accentColor + '18', border: `1px solid ${accentColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={accentColor} />
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <TrendingUp size={12} color={accentColor} />
      <span style={{ fontSize: 11, fontWeight: 600, color: accentColor }}>{trend}</span>
    </div>
  </div>
);

// ─── Custom Blinking Icon ─────────────────────────────────────────
const blinkingIcon = new L.DivIcon({
  className: 'transparent-marker', 
  html: `<div class="nd-map-marker" style="width: 14px; height: 14px; margin-left: -7px; margin-top: -7px;"></div>`,
  iconSize: [0, 0], // Center exactly on coordinates
});


// ─── Main Component ───────────────────────────────────────────────
const CIAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [businessStats, setBusinessStats] = useState({ totalRevenue: 0, totalCapacityMW: 0, totalClients: 0, commercialCount: 0, industrialCount: 0 });
  const [operationStats, setOperationStats] = useState({ scheduledCount: 0, completedMonth: 0, pendingMonth: 0, completionRate: 0, missedJobs: 0 });
  const [todayPendingJobs, setTodayPendingJobs] = useState([]);
  const [teamStats, setTeamStats] = useState({ totalTeams: 0, totalDailyCapacity: 0, activeTeams: 0 });
  const [monthlyJobData, setMonthlyJobData] = useState([]);
  
  // Naya state actual sites ke liye
  const [mapSites, setMapSites] = useState([]);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  useEffect(() => { fetchDashboardData(); }, [selectedMonth, selectedYear]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [clientsRes, teamsRes, calendarRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams?active_only=true`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/calendar/${selectedMonth}/${selectedYear}`)
      ]);

      const clients = clientsRes.data.data?.clients || [];
      const totalRev = clients.reduce((sum, c) => sum + (parseFloat(c.sales_price_before_tax) || 0), 0);
      const totalCap = clients.reduce((sum, c) => sum + (parseFloat(c.total_system_size) || 0), 0);
      setBusinessStats({ totalRevenue: totalRev, totalCapacityMW: (totalCap / 1000).toFixed(2), totalClients: clients.length, commercialCount: clients.filter(c => c.contract_type === 'commercial').length, industrialCount: clients.filter(c => c.contract_type === 'industrial').length });

      // ─── MAP SITES DATA EXTRACTION ───
      // Aapke backend mein agar lat/lng fields ka naam alag hai (jese latitude/longitude), 
      // tou is logic ko adjust kar lijiye ga. Abhi yeh dono cases handle kar lega.
      let extractedSites = clients
        .filter(c => (c.latitude || c.lat) && (c.longitude || c.lng || c.long)) // Sirf un sites ko le jinme coordinates hain
        .map(c => ({
          id: c.id,
          name: c.site_name || c.company_name || c.client_name || 'Client Site',
          lat: parseFloat(c.latitude || c.lat),
          lng: parseFloat(c.longitude || c.lng || c.long),
          type: c.contract_type || 'Unknown'
        }));
      
      // Agar API mein abhi coordinates nahi hain, tou testing ke liye fallback (dummy data) use karega
      if (extractedSites.length === 0) {
        extractedSites = [
          { id: 991, name: "Fallback Site 1 (Add real lat/lng in DB)", lat: 24.8916, lng: 67.0681, type: "Commercial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.8920, lng: 67.0747, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 34.1827621, lng: 72.6801083, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 31.5392, lng: 74.3550, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 31.33, lng: 74.15, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 25.011, lng:  67.818, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.8555, lng:  67.2118, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 25.044, lng: 67.098, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.8661, lng: 67.0785, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.9515, lng: 67.0808, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.8465, lng: 67.1325, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 25.0039, lng: 67.0966, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 31.1303, lng: 74.3585, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 30.1239, lng: 71.3739, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 31.69, lng: 74.15, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 31.56, lng: 74.33, type: "Industrial" },
          { id: 992, name: "Fallback Site 2 (Add real lat/lng in DB)", lat: 24.8661, lng: 67.0785, type: "Industrial" },
        ];
      }
      setMapSites(extractedSites);
      // ────────────────────────────────

      const teams = teamsRes.data.teams || [];
      const dailyCap = teams.reduce((sum, t) => sum + (parseInt(t.max_daily_capacity) || 0), 0);
      setTeamStats({ totalTeams: teams.length, totalDailyCapacity: dailyCap, activeTeams: teams.length });

      const calendarClients = calendarRes.data.clients || [];
      let totalScheduled = 0, totalCompleted = 0, missedCount = 0;
      let pendingJobsList = [];
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const JobsPerDay = {};
      for (let i = 1; i <= daysInMonth; i++) JobsPerDay[i] = 0;

      const todayDateObj = new Date();
      const todayYYYYMMDD = `${todayDateObj.getFullYear()}-${String(todayDateObj.getMonth()+1).padStart(2,'0')}-${String(todayDateObj.getDate()).padStart(2,'0')}`;

      calendarClients.forEach(client => {
        if (client.schedules && Array.isArray(client.schedules)) {
          client.schedules.forEach(Job => {
            const JobDate = Job.scheduled_date ? Job.scheduled_date.split('T')[0] : null;
            totalScheduled++;
            if (Job.status === 'completed') totalCompleted++;
            const JobDateObj = new Date(JobDate);
            const today = new Date(); today.setHours(0,0,0,0);
            if (Job.status === 'missed' || (Job.status !== 'completed' && JobDateObj < today && selectedMonth <= (today.getMonth()+1))) missedCount++;
            if (JobDate === todayYYYYMMDD && Job.status !== 'completed' && Job.status !== 'started' && Job.status !== 'in_progress') {
              pendingJobsList.push({ clientName: client.site_name || client.client_name || client.company_name || client.name || 'Client', jobId: Job.id || '-', status: Job.status || 'pending', type: Job.job_type || 'N/A' });
            }
            if (JobDate) { const day = new Date(JobDate).getDate(); if (JobsPerDay[day] !== undefined) JobsPerDay[day]++; }
          });
        }
      });

      setOperationStats({ scheduledCount: totalScheduled, completedMonth: totalCompleted, pendingMonth: totalScheduled - totalCompleted, completionRate: totalScheduled > 0 ? Math.round((totalCompleted / totalScheduled) * 100) : 0, missedJobs: missedCount });
      setTodayPendingJobs(pendingJobsList);
      setMonthlyJobData(Object.keys(JobsPerDay).map(day => ({ name: `${day}`, Jobs: JobsPerDay[day] })));
    } catch (err) {
      console.error("Dashboard Data Fetch Error:", err);
      setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const loadPercent = teamStats.totalDailyCapacity > 0
    ? Math.min(100, Math.round((Math.round(operationStats.scheduledCount / 30) / teamStats.totalDailyCapacity) * 100))
    : 0;

  return (
    <div className="novaa-dash" style={{ background: T.bg, padding: '14px 20px' }}>
      <style>{DASH_STYLES}</style>

      {/* ══ WAR-ROOM HEADER ══ */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 16, overflow: 'hidden' }}>

        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>
              CI Admin Dashboard
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Performance Overview & Historical Data</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarIcon size={16} color={T.muted} />
            <select className="nd-month-select" value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
              {monthNames.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select className="nd-month-select" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={{ width: 80 }}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className="nd-refresh-btn" onClick={fetchDashboardData} title="Refresh">
              <Activity size={15} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          {[
            { label: 'CLIENTS',     value: businessStats.totalClients,        color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.03)' },
            { label: 'CAPACITY',    value: `${businessStats.totalCapacityMW}MW`, color: '#F59E0B', bg: 'rgba(245,158,11,0.06)' },
            { label: 'TOTAL JOBS',  value: operationStats.scheduledCount,     color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.02)' },
            { label: 'COMPLETED',   value: operationStats.completedMonth,     color: '#22D3A5', bg: 'rgba(34,211,165,0.06)' },
            { label: 'PENDING',     value: operationStats.pendingMonth,       color: '#60A5FA', bg: 'rgba(96,165,250,0.06)' },
            { label: 'MISSED',      value: operationStats.missedJobs,         color: '#F87171', bg: 'rgba(248,113,113,0.06)' },
            { label: 'COMPLETION',  value: `${operationStats.completionRate}%`, color: operationStats.completionRate >= 70 ? '#22D3A5' : operationStats.completionRate >= 40 ? '#F59E0B' : '#F87171', bg: 'rgba(255,255,255,0.02)' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ flex: 1, padding: '14px 0', textAlign: 'center', background: bg, borderRight: `1px solid ${T.border}`, borderBottom: `2px solid ${color}40` }}>
              <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>
                {loading ? '—' : value}
              </div>
              <div style={{ fontSize: 8, color: T.muted, letterSpacing: '0.15em', marginTop: 4, fontWeight: 700 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.08)', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ fontSize: 12, color: T.muted, letterSpacing: '0.1em' }}>
            Loading {monthNames[selectedMonth-1]} {selectedYear}…
          </div>
        </div>
      ) : (
        <>
          {todayPendingJobs.length > 0 && (
            <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}
              className="fade-up">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={18} color="#F59E0B" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#F59E0B' }}>Attention Needed</div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>
                    Today has <strong style={{ color: T.text }}>{todayPendingJobs.length} job{todayPendingJobs.length !== 1 ? 's' : ''}</strong> still pending
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>
                {todayPendingJobs.map((job, idx) => {
                  const st = statusStyle(job.status);
                  return (
                    <div key={idx} className="nd-pending-row">
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{job.clientName}</div>
                      <span className="nd-status-badge" style={{ color: st.color, background: st.bg, border: `1px solid ${st.color}30` }}>
                        {job.status.replace('_',' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Row className="g-3 mb-4">
            <Col lg={3} md={6}>
              <StatCard title="Managed Capacity" value={`${businessStats.totalCapacityMW} MW`} icon={Zap} accentColor="#F59E0B" trend={`${businessStats.totalClients} Active Clients`} stagger={1} />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title={`${monthNames[selectedMonth-1]} Completion`} value={`${operationStats.completionRate}%`} icon={CheckCircle} accentColor="#3B82F6" trend={`${operationStats.completedMonth} / ${operationStats.scheduledCount} Jobs`} stagger={2} />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title="Teams Deployed" value={teamStats.totalTeams} icon={Users} accentColor="#22D3A5" trend={`${teamStats.totalDailyCapacity} daily capacity`} stagger={3} />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title="Missed / Overdue" value={operationStats.missedJobs} icon={AlertTriangle} accentColor="#F87171" trend="Action Required" stagger={4} />
            </Col>
          </Row>

          <Row className="g-3">
            <Col lg={8}>
              <div className="nd-chart-card fade-up">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <div className="nd-section-label">JOB EXECUTION FLOW</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', marginTop: -8 }}>
                      Daily Breakdown · {monthNames[selectedMonth-1]} {selectedYear}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#60A5FA', fontFamily: "'DM Mono', monospace" }}>
                      {operationStats.scheduledCount}
                    </div>
                    <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.1em' }}>TOTAL</div>
                  </div>
                </div>
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyJobData} barSize={14}>
                      <defs>
                        <linearGradient id="novaaBarGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity={1} />
                          <stop offset="100%" stopColor="#22D3A5" stopOpacity={0.7} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10, fontFamily: "'DM Mono', monospace" }} interval={2} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }} />
                      <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                      <Bar dataKey="Jobs" fill="url(#novaaBarGrad)" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                <div className="nd-chart-card fade-up" style={{ flex: '0 0 auto' }}>
                  <div className="nd-section-label">CLIENT PORTFOLIO</div>
                  <div style={{ height: 170, position: 'relative' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[{ name: 'Commercial', value: businessStats.commercialCount }, { name: 'Industrial', value: businessStats.industrialCount }]}
                          cx="50%" cy="50%" innerRadius={52} outerRadius={70} paddingAngle={4} dataKey="value" startAngle={90} endAngle={-270}>
                          <Cell fill="#3B82F6" />
                          <Cell fill="#22D3A5" />
                        </Pie>
                        <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#E2E8F0', fontFamily: "'DM Sans', sans-serif", fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{businessStats.totalClients}</div>
                      <div style={{ fontSize: 8, color: T.muted, letterSpacing: '0.12em', marginTop: 2 }}>CLIENTS</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                    {[['#3B82F6','Commercial',businessStats.commercialCount],['#22D3A5','Industrial',businessStats.industrialCount]].map(([color, label, count]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: color, boxShadow: `0 0 6px ${color}` }} />
                        <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{label} ({count})</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="nd-chart-card fade-up" style={{ flex: 1 }}>
                  <div className="nd-section-label">OPERATIONAL CAPACITY</div>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>Daily Load</span>
                      <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
                        {Math.round(operationStats.scheduledCount / 30)} / {teamStats.totalDailyCapacity}
                      </span>
                    </div>
                    <div className="nd-progress-track">
                      <div className="nd-progress-fill" style={{ width: `${loadPercent}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>{loadPercent}% of daily capacity used</div>
                  </div>

                  <div className="nd-capacity-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={18} color="#22D3A5" />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>Active Teams</div>
                        <div style={{ fontSize: 10, color: T.muted }}>Field Force</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#22D3A5', fontFamily: "'DM Mono', monospace" }}>
                      {teamStats.activeTeams}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                    {[
                      ['Commercial', businessStats.commercialCount, '#3B82F6'],
                      ['Industrial', businessStats.industrialCount, '#22D3A5'],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ padding: '10px 14px', borderRadius: 10, background: color+'10', border: `1px solid ${color}25`, textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'DM Mono', monospace" }}>{val}</div>
                        <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.1em', marginTop: 2, fontWeight: 700 }}>{label.toUpperCase()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* ══ MAP SECTION ══ */}
          <Row className="g-3 mt-1 fade-up stagger-4">
            <Col lg={12}>
              <div className="nd-chart-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.01)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Map size={20} color="#E2E8F0" />
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
                      Interactive Site Locations
                    </div>
                  </div>
                  <div style={{ background: 'rgba(14, 165, 233, 0.15)', color: '#38BDF8', padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "'DM Mono', monospace", border: '1px solid rgba(14, 165, 233, 0.3)' }}>
                    {mapSites.length} SITES PLOTTED
                  </div>
                </div>

                <div style={{ height: 400, width: '100%', position: 'relative', zIndex: 0 }}>
                  <MapContainer 
                    center={[30.3753, 69.3451]} 
                    zoom={5} 
                    scrollWheelZoom={true} 
                    style={{ height: '100%', width: '100%', borderRadius: '0 0 14px 14px' }}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                     
                    />

                    {/* CircleMarker hata kar Custom HTML Marker lagaya hai takay CSS Animation (Blink) chal sakay */}
                    {mapSites.map(site => (
                      <Marker 
                        key={site.id} 
                        position={[site.lat, site.lng]} 
                        icon={blinkingIcon}
                      >
                        <Popup>
                          <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#333' }}>
                            <strong style={{ fontSize: 14 }}>{site.name}</strong><br/>
                            <span style={{ fontSize: 12, color: '#666' }}>Type: {site.type}</span>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </div>
            </Col>
          </Row>
          
        </>
      )}
    </div>
  );
};

export default CIAdminDashboard;